import { useLazyQuery } from "@apollo/client";
import {
  SearchInput,
  SearchResponse,
  SearchResultItem,
  SuggestType,
} from "@towersystems/roam-common/lib/generated-types";
import { useCallback, useContext, useEffect, useState } from "react";
import { useScheduler } from "../scheduler";
import useOnline from "@towersystems/roam-common-native/lib/utilities/use-online";
import { CompleteQuerySearch, QuerySearch } from "./constants";
import {
  FulltextCustomerSearchContext,
  FulltextProductSearchContext,
  FulltextSearchContext,
  FulltextSearchContextType,
} from "./context";

export type FullTextSearchOptions = {
  type?: SuggestType;
  responseType?: "summary" | "complete";
  activeOnly?: boolean;
  headers?: Record<string, string>;
  loadEmptyQuery?: boolean;
};

export const useFullTextSearch = ({
  type = SuggestType.PRODUCT,
  responseType = "summary",
  activeOnly = false,
  headers = {},
  loadEmptyQuery = false,
}: FullTextSearchOptions) => {
  const online = useOnline();
  const [fauxLoading, setFauxLoading] = useState(false);
  const [items, setItems] = useState<SearchResultItem[]>([]);
  const [query, _setQuery] = useState("");
  const [executedQuery, _setExecutedQuery] = useState("");
  const { schedule } = useScheduler(200);
  const [executeSearch, { data: response, loading }] = useLazyQuery<
    { search: SearchResponse },
    { input: SearchInput }
  >(responseType == "summary" ? QuerySearch : CompleteQuerySearch, {
    context: {
      headers: { ...headers },
    },
  });

  useEffect(() => {
    if (loadEmptyQuery || (query && online)) {
      setFauxLoading(true);
      setItems([]);
      schedule(() => {
        _setExecutedQuery(query);
        executeSearch({
          variables: {
            input: {
              term: query,
              type,
              activeOnly,
            },
          },
        }).catch((i) => setFauxLoading(false));
      });
    } else if (query == "") {
      setFauxLoading(false);
      _setExecutedQuery("");
      setItems([]);
    }
  }, [query]);

  useEffect(() => {
    if (response?.search.items) {
      setItems(response?.search.items);
      setFauxLoading(false);
    }
  }, [response?.search.items]);

  const setQuery = useCallback(
    (v: string) => {
      _setQuery(v);
    },
    [_setQuery]
  );

  const clearSearchValue = useCallback(() => {
    _setQuery("");
  }, [_setQuery]);

  const context: FulltextSearchContextType = {
    loading: loading || fauxLoading,
    query,
    executedQuery,
    setQuery,
    clearSearchValue,
    items,
  };

  return context;
};

export const useFullTextSearchProvider = () => {
  const context = useContext(FulltextSearchContext);
  if (!context) {
    throw new Error("No FulltextSearchContext context was provided. ");
  }
  return context;
};

export const useFullTextCustomerSearchProvider = () => {
  const context = useContext(FulltextCustomerSearchContext);
  if (!context) {
    throw new Error("No FulltextCustomerSearchContext context was provided. ");
  }
  return context;
};

export const useFullTextProductSearchProvider = () => {
  const context = useContext(FulltextProductSearchContext);
  if (!context) {
    throw new Error("No FulltextProductSearchContext context was provided. ");
  }
  return context;
};
